import { VNode, defineComponent, h } from 'vue'

import { useUserHelpers } from '../../../composables/useUserHelpers'
import { slot } from '../utils'
import { BsBtn } from '../index'

/**
 * Gates a feature behind a plan: renders its slot when the account has the permission, and an explanation
 * plus an upgrade button when it does not.
 *
 * It took `CommonAuthUserMixin` rather than the bundle's base — it needs `authenticatedUser` and
 * `hasTieredPermission` and nothing else, and extending the full base mixin made this file the bundle's
 * only outward edge, which created the two import cycles described in authUser.ts. `useUserHelpers` is
 * that same pair without the class.
 *
 * The upgrade link differs by plan: an account already paying goes straight to the change-plan flow for
 * the role it needs, and a free one to the plan picker.
 */
export default defineComponent({
  name: 'BsUpgrade',

  props: {
    role: { type: String, required: true },
    permissionName: { type: String, required: true },
    params: { type: Object, default: void 0 },
    /** Used for styling (renders component in horizontal mode) */
    inline: { type: Boolean, default: false }
  },

  setup () {
    return { ...useUserHelpers() }
  },

  methods: {
    /** Renamed from `__renderTitle`: Vue does not proxy keys beginning with `_`. */
    renderTitle (): VNode | undefined {
      const translationKey = `system.permissions.${this.permissionName}.title`

      // Not every permission has a title, and a missing key would render as the key itself.
      if (!this.$te(translationKey)) {
        return void 0
      }

      const limit = this.params?.copyCountLimit === -1
        ? this.$tc('system.labels.unlimited')
        : this.params?.copyCountLimit

      return h('strong', this.$t(translationKey, {
        maxParticipantCount: limit
      }).toString())
    },

    renderContent (hasPermission: boolean): (VNode | undefined)[] {
      if (this.role === void 0) {
        // `[void 0]`, as this was, renders a comment placeholder under Vue 3 rather than nothing.
        return []
      }

      const roleFriendlyName = this.$tc(`system.labels.roles.author.${this.role}`)
      const explanationText = this.$t(`system.permissions.${this.permissionName}.explanation`, {
        planName: roleFriendlyName.toLowerCase(),
        maxRunningArcsCount: this.params?.maxRunningArcsCount
      })
      const settingsUrl = this.authenticatedUser.permissionQuery.isOnPaidPlan
        ? `/settings/billing/select-plan/${this.role}/month/upgrade`
        : '/settings/billing/select-plan'

      // The class re-checked `role !== void 0` here and had an unreachable `return [void 0]` after it —
      // the guard above has already returned for that case.
      if (hasPermission) {
        return slot(this, 'default')
      }

      return [
        this.renderTitle(),
        h('span', explanationText.toString()),
        h(BsBtn, {
          to: settingsUrl,
          label: this.$t('system.actions.upgrade')
        })
      ]
    }
  },

  render () {
    const hasPermission = this.hasTieredPermission(this.role)

    return h('div', {
      class: [hasPermission ? '' : 'bs-upgrade', !hasPermission && this.inline ? 'bs-upgrade__inline' : ''],
      // Not `!hasPermission && permissionName`: Vue 3 renders a `false` attribute as "false".
      'data-cy': hasPermission ? void 0 : this.permissionName
    }, this.renderContent(hasPermission))
  }
})
